<template>
  <div class="markdown-content" v-html="renderedContent"></div>
</template>

<script>
export default {
  name: 'SimpleMarkdownRenderer',
  props: {
    content: {
      type: String,
      default: ''
    }
  },
  computed: {
    renderedContent() {
      if (!this.content) return '';
      
      try {
        return this.parseMarkdown(this.content);
      } catch (error) {
        console.error('Markdown 解析失败:', error);
        return this.escapeHtml(this.content).replace(/\n/g, '<br>');
      }
    }
  },
  methods: {
    // 转义 HTML 特殊字符
    escapeHtml(text) {
      const map = {
        '&': '&',
        '<': '<',
        '>': '>',
        '"': '"',
        "'": '&#039;'
      };
      return text.replace(/[&<>"']/g, m => map[m]);
    },
    
    // 解析 Markdown 文本为 HTML
    parseMarkdown(text) {
      if (!text) return '';
      
      let html = text;
      
      // 1. 转义 HTML 特殊字符
      html = this.escapeHtml(html);
      
      // 2. 处理标题 (#, ##, ###, ####, #####, ######)
      html = html.replace(/^###### (.*$)/gim, '<h6>$1</h6>');
      html = html.replace(/^##### (.*$)/gim, '<h5>$1</h5>');
      html = html.replace(/^#### (.*$)/gim, '<h4>$1</h4>');
      html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
      html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
      html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');
      
      // 3. 处理粗体 (**text**)
      html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      
      // 4. 处理斜体 (*text*)
      html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
      
      // 5. 处理代码块 (```code```)
      html = html.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>');
      
      // 6. 处理行内代码 (`code`)
      html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
      
      // 7. 处理无序列表
      html = html.replace(/^\s*[-*+] (.*$)/gim, '<li>$1</li>');
      html = html.replace(/(<li>.*<\/li>)/gim, '<ul>$1</ul>');
      
      // 8. 处理有序列表
      html = html.replace(/^\s*\d+\. (.*$)/gim, '<li>$1</li>');
      html = html.replace(/(<li>.*<\/li>)/gim, '<ol>$1</ol>');
      
      // 9. 处理引用 (> text)
      html = html.replace(/^> (.*$)/gim, '<blockquote>$1</blockquote>');
      
      // 10. 处理水平线 (---)
      html = html.replace(/^---$/gim, '<hr>');
      
      // 11. 处理链接 ([text](url))
      html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
      
      // 12. 处理图片 (![alt](src))
      html = html.replace(/!\[([^\]]+)\]\(([^)]+)\)/g, '<img src="$2" alt="$1">');
      
      // 13. 处理换行符
      html = html.replace(/\n\n/g, '</p><p>');
      html = html.replace(/\n/g, '<br>');
      
      // 14. 包装段落
      if (!html.startsWith('<h') && !html.startsWith('<ul') && !html.startsWith('<ol') && 
          !html.startsWith('<blockquote') && !html.startsWith('<pre') && !html.startsWith('<hr')) {
        html = '<p>' + html + '</p>';
      }
      
      // 15. 清理多余的段落标签
      html = html.replace(/<\/p><p>/g, '</p>\n<p>');
      html = html.replace(/<p><\/p>/g, '');
      
      return html;
    }
  }
}
</script>

<style scoped>
.markdown-content {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  line-height: 1.6;
  color: #333;
}

.markdown-content >>> h1,
.markdown-content >>> h2,
.markdown-content >>> h3,
.markdown-content >>> h4,
.markdown-content >>> h5,
.markdown-content >>> h6 {
  margin-top: 1.5em;
  margin-bottom: 0.5em;
  font-weight: 600;
  line-height: 1.25;
}

.markdown-content >>> h1 {
  font-size: 1.5em;
  border-bottom: 1px solid #eaecef;
  padding-bottom: 0.3em;
}

.markdown-content >>> h2 {
  font-size: 1.25em;
  border-bottom: 1px solid #eaecef;
  padding-bottom: 0.3em;
}

.markdown-content >>> h3 {
  font-size: 1.1em;
}

.markdown-content >>> p {
  margin-top: 0;
  margin-bottom: 1em;
}

.markdown-content >>> ul,
.markdown-content >>> ol {
  margin-top: 0;
  margin-bottom: 1em;
  padding-left: 2em;
}

.markdown-content >>> li {
  margin-bottom: 0.25em;
}

.markdown-content >>> blockquote {
  margin: 1em 0;
  padding: 0.5em 1em;
  border-left: 4px solid #dfe2e5;
  color: #6a737d;
  background-color: #f6f8fa;
}

.markdown-content >>> code {
  padding: 0.2em 0.4em;
  margin: 0;
  font-size: 85%;
  background-color: rgba(27,31,35,0.05);
  border-radius: 3px;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
}

.markdown-content >>> pre {
  margin: 1em 0;
  padding: 1em;
  overflow: auto;
  font-size: 85%;
  line-height: 1.45;
  background-color: #f6f8fa;
  border-radius: 3px;
}

.markdown-content >>> pre code {
  padding: 0;
  margin: 0;
  font-size: 100%;
  background-color: transparent;
  border: 0;
}

.markdown-content >>> table {
  display: block;
  width: 100%;
  overflow: auto;
  margin: 1em 0;
  border-collapse: collapse;
}

.markdown-content >>> table th,
.markdown-content >>> table td {
  padding: 0.5em 1em;
  border: 1px solid #dfe2e5;
}

.markdown-content >>> table th {
  font-weight: 600;
  background-color: #f6f8fa;
}

.markdown-content >>> table tr:nth-child(2n) {
  background-color: #f6f8fa;
}

.markdown-content >>> a {
  color: #0366d6;
  text-decoration: none;
}

.markdown-content >>> a:hover {
  text-decoration: underline;
}

.markdown-content >>> hr {
  height: 0.25em;
  padding: 0;
  margin: 1.5em 0;
  background-color: #e1e4e8;
  border: 0;
}

/* 深色模式支持 */
.dark .markdown-content {
  color: #e5e7eb;
}

.dark .markdown-content >>> h1,
.dark .markdown-content >>> h2 {
  border-bottom-color: #374151;
}

.dark .markdown-content >>> blockquote {
  border-left-color: #4b5563;
  color: #9ca3af;
  background-color: #1f2937;
}

.dark .markdown-content >>> code {
  background-color: rgba(75, 85, 99, 0.5);
}

.dark .markdown-content >>> pre {
  background-color: #1f2937;
}

.dark .markdown-content >>> table th,
.dark .markdown-content >>> table td {
  border-color: #4b5563;
}

.dark .markdown-content >>> table th {
  background-color: #1f2937;
}

.dark .markdown-content >>> table tr:nth-child(2n) {
  background-color: #1f2937;
}

.dark .markdown-content >>> a {
  color: #60a5fa;
}

.dark .markdown-content >>> hr {
  background-color: #4b5563;
}
</style>